/**
 * Authentication Service for BPV Ecom
 * Supports same login methods as main website:
 * - Customer login with Card No (Customer ID)
 * - Promoter login with Promoter ID
 */
import { supabase } from './supabaseClient';

class AuthService {
  constructor() {
    this.currentUser = null;
    this.currentSession = null;
    this.storageKey = 'bpv_ecom_auth_state';
    this.loadPersistedState();
  }

  loadPersistedState() {
    try {
      const persisted = localStorage.getItem(this.storageKey);
      if (persisted) {
        const { user, session } = JSON.parse(persisted);
        if (session && session.expires_at > Math.floor(Date.now() / 1000)) {
          this.currentUser = user;
          this.currentSession = session;
        } else {
          localStorage.removeItem(this.storageKey);
        }
      }
    } catch (error) {
      console.error('Failed to load persisted state:', error);
    }
  }

  persistState() {
    try {
      if (this.currentUser && this.currentSession) {
        localStorage.setItem(this.storageKey, JSON.stringify({
          user: this.currentUser,
          session: this.currentSession
        }));
      } else {
        localStorage.removeItem(this.storageKey);
      }
    } catch (error) {
      console.error('Failed to persist auth state:', error);
    }
  }

  /**
   * Customer login with Card No (Customer ID) and password
   */
  async loginWithCardNo(cardNo, password) {
    try {
      if (!cardNo || !password) {
        throw new Error('Please provide both Customer ID and password');
      }

      const { data, error } = await supabase.rpc('authenticate_customer_by_card_no', {
        p_customer_id: cardNo,
        p_password: password
      });

      if (error) {
        throw new Error('Authentication failed. Please check your Customer ID and password.');
      }

      if (!data || !data.success) {
        throw new Error(data?.error || 'Invalid Customer ID or password');
      }

      const userProfile = data.user;

      const localSession = {
        access_token: `local_session_${userProfile.id}`,
        token_type: 'bearer',
        user: { id: userProfile.id, email: data.auth_email },
        expires_at: Math.floor(Date.now() / 1000) + (3600 * 24)
      };

      this.currentUser = userProfile;
      this.currentSession = localSession;
      this.persistState();

      return userProfile;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Promoter login with Promoter ID and password
   */
  async loginWithPromoterID(promoterID, password) {
    try {
      if (!promoterID || !password) {
        throw new Error('Please provide both Promoter ID and password');
      }

      if (!/^BPVP\d+$/i.test(promoterID)) {
        throw new Error('Invalid Promoter ID format. Expected format: BPVP01, BPVP02, etc.');
      }

      const { data, error } = await supabase.rpc('authenticate_promoter_by_id_only', {
        p_promoter_id: promoterID.toUpperCase(),
        p_password: password
      });

      if (error) {
        throw new Error('Authentication failed. Please check your Promoter ID and password.');
      }

      if (!data || !data.success) {
        throw new Error(data?.error || 'Invalid Promoter ID or password');
      }

      const userProfile = data.user;

      const localSession = {
        access_token: `local_session_${userProfile.id}`,
        token_type: 'bearer',
        user: { id: userProfile.id, email: data.auth_email },
        expires_at: Math.floor(Date.now() / 1000) + (3600 * 24)
      };

      this.currentUser = userProfile;
      this.currentSession = localSession;
      this.persistState();

      return userProfile;
    } catch (error) {
      throw error;
    }
  }

  async logout() {
    try {
      await supabase.auth.signOut();
    } catch (error) {
      console.error('Logout error:', error);
    }
    this.currentUser = null;
    this.currentSession = null;
    localStorage.removeItem(this.storageKey);
    sessionStorage.clear();
  }

  getCurrentUser() {
    return this.currentUser;
  }

  getCurrentSession() {
    return this.currentSession;
  }

  isAuthenticated() {
    return !!this.currentUser && !!this.currentSession;
  }
}

const authService = new AuthService();
export default authService;
